"use client";

import * as React from "react";
import { Check } from "lucide-react";

import type { SupportedLang } from "@acme/validators";
import { languageDisplayNames } from "@acme/ast-grep-wasm-loader";
import { cn } from "@acme/ui";
import { Button } from "@acme/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@acme/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@acme/ui/popover";
import { ScrollArea } from "@acme/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@acme/ui/tooltip";

import { umami } from "~/utils/analytics";
import { getLanguageIcon } from "../../_utils/languages";

interface LanguageSelectorProps {
  onChange: (lang: SupportedLang) => void;
  value: SupportedLang;
}

export function LanguageSelector({ onChange, value }: LanguageSelectorProps) {
  const [open, setOpen] = React.useState(false);

  const handleLanguageChange = (lang: string) => {
    if (value !== lang) {
      void umami.track("studio:change-language", {
        from: value,
        to: lang,
      });
      setOpen(false);
      onChange(lang as SupportedLang);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant={open ? "secondary" : "ghost"}
              size="xs"
              className="font-mono"
            >
              {getLanguageIcon(value, "size-4")}
              {value}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <span className="font-bold">Language</span>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="bottom"
        align="start"
        className="h-134 w-52 rounded-lg p-0 text-sm shadow-lg dark:shadow-2xl"
      >
        <Command className="flex h-full rounded-none bg-transparent">
          <CommandInput placeholder="Search language..." />
          <ScrollArea className="h-full flex-1">
            <CommandList className="h-full max-h-full">
              <CommandEmpty>No language found.</CommandEmpty>
              <CommandGroup>
                {Object.entries(languageDisplayNames).map(([lang, name]) => (
                  <CommandItem
                    key={lang}
                    onSelect={() => handleLanguageChange(lang)}
                    className={cn(
                      "mx-1 my-1 flex items-center justify-between px-1.5 py-0.5",
                      value === lang && "ring-primary/20 !bg-primary/5 ring-1",
                    )}
                  >
                    <div className="inline-flex items-center gap-2">
                      {getLanguageIcon(lang as SupportedLang, "size-4")}
                      <span className="font-mono">{name}</span>
                    </div>
                    {value === lang && (
                      <Check className={cn("text-primary size-3")} />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
